import {unwrapResult} from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {requestOK} from 'redux-thunk-kit';
import CustomDropdown from '~/components/dropdowns/CustomDropdown';
import {
  AppDatePicker,
  Col,
  UiDivider,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiInput,
  UiQuestionnaireLayout,
  UiText,
} from '~/components/elements';
import {UiCard} from '~/components/elements/UiCard';
import {PAGE_URLS} from '~/constants';
import {
  SEX_OPTIONS,
  WEIGHT_LOSS_KEYS,
  WEIGHT_LOSS_ONE_QUESTIONNAIRES,
  WL_PROGRAMS,
} from '~/constants/medical-questionnaires';
import {useInput, useRequestState} from '~/hooks';
import {setMedicalUUID, setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {UserProfile} from '~/types/models/questionnaire';
import {
  DATE_OF_BIRTH_TIME_FORMAT,
  formatDate,
  isUnderAge,
  LONG_MONTH_NAME_FORMAT,
  redirectInIframe,
} from '~/utils/helpers';

interface Props {}

const Questionnaire3Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  const [redirectLoading, setRedirectLoading] = useState(false);
  const {loading} = useRequestState(submitOrLogMdq);

  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_KEYS.PROFILE);

  const currentUser = useSelector(
    (state: RootState) => state.questionnaires.collectedData[WEIGHT_LOSS_KEYS.PROFILE]?.data?.user,
  );

  const rules = {
    fullName: {
      presence: {allowEmpty: false},
    },
    dateOfBirth: {presence: true},
    sex: {presence: true},
    email: {presence: {allowEmpty: false}, email: true},
    phoneNumber: {presence: {allowEmpty: false}},
  };
  const {inputs, setInputs, validation, trimSpaces} = useInput<UserProfile>(
    {
      fullName: currentUser?.fullName || '',
      dateOfBirth: currentUser?.dateOfBirth,
      sex: currentUser?.sex || '',
      email: currentUser?.email || '',
      phoneNumber: currentUser?.phoneNumber || '',
    },
    {rules},
  );

  const isAllInputsFilled = inputs.fullName && inputs.dateOfBirth && inputs.sex && inputs.email && inputs.phoneNumber;

  const collectedData = mdq.collectedData;

  const onClickSubmit = async () => {
    if (validation.getErrors()) {
      return;
    }

    const answerData = {
      key: WEIGHT_LOSS_KEYS.PROFILE,
      index: questionnaire?.index || '',
      data: {user: {...inputs, fullName: trimSpaces(inputs.fullName)}},
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));

    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.PROFILE]: answerData,
          },
        },
      }),
    );

    if (isUnderAge(inputs.dateOfBirth)) {
      history.push(PAGE_URLS.MDQ1_AGE_GENDER_BLOCK);
      return;
    }

    // if (isGCM) {
    //   const res = await dispatch(
    //     submitOrLogMdq({
    //       data: {
    //         uid: medicalUUID,
    //         methodKey: methodKey.toLowerCase(),
    //         status: 'submitted',
    //         data: {
    //           ...collectedData,
    //           [WEIGHT_LOSS_KEYS.PROFILE]: answerData,
    //         },
    //       },
    //     }),
    //   );

    //   if (requestOK(res, submitOrLogMdq)) {
    //     const result = unwrapResult(res);
    //     if (result.getCheckoutUrl) {
    //       setRedirectLoading(true);
    //       redirectInIframe(result.getCheckoutUrl);
    //     }
    //   }

    //   if (res.payload?.errMsg === 'Medical Form invalid') {
    //     dispatch(setMedicalUUID({uid: null}));
    //     history.replace(PAGE_URLS.MDQ1_HOME);
    //   }
    //   return;
    // }

    history.push(PAGE_URLS.MDQ1_HOW_DID_YOU_HEAR);
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      disableNext={!isAllInputsFilled || redirectLoading || loading}
      loading={redirectLoading || loading}
      pageTitle={questionnaire?.title}
    >
      <Col mt={14} px={2} textAlign="center">
        <UiText fontSize="xl" fontWeight="bold">
          {questionnaire?.questions?.default}
        </UiText>
        <UiCard mt={4} borderRadius="1rem" boxShadow="0px 6px 25px 0px rgba(0, 0, 0, 0.10)" pt={4} pb={9}>
          <UiFormControl id="fullName" isRequired isInvalid={!!validation?.errors?.fullName}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Full Name
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.fullName}
              onChange={({target}) => setInputs({fullName: target.value})}
              fontWeight="600"
            />
            {validation?.errors?.fullName && <UiFormErrorMessage>{validation?.errors?.fullName[0]}</UiFormErrorMessage>}
          </UiFormControl>
          <UiFormControl id="dateOfBirth" isRequired isInvalid={!!validation?.errors?.dateOfBirth}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Date of Birth
            </UiFormLabel>
            <AppDatePicker
              selected={!!inputs.dateOfBirth ? moment(inputs?.dateOfBirth) : undefined}
              onChange={value => {
                setInputs({dateOfBirth: formatDate(value)});
              }}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              customInput={<UiInput variant="flushed" fontWeight="600" />}
              dateFormat={DATE_OF_BIRTH_TIME_FORMAT}
              maxDate={new Date()}
            />
            {validation?.errors?.dateOfBirth && (
              <UiFormErrorMessage>{validation?.errors?.dateOfBirth[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
          <UiFormControl id="sex" isRequired isInvalid={!!validation?.errors?.sex}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Sex
            </UiFormLabel>
            <CustomDropdown
              onChange={value => setInputs({sex: value?.value})}
              options={SEX_OPTIONS}
              defaultItem={SEX_OPTIONS.find(i => i.value === inputs.sex)}
              selectedFontWeight="600"
            />
            {/* <UiInput
              variant="flushed"
              value={inputs.sex}
              onChange={({target}) => setInputs({sex: target.value})}
              fontWeight="600"
            /> */}
            {validation?.errors?.sex && <UiFormErrorMessage>{validation?.errors?.sex[0]}</UiFormErrorMessage>}
          </UiFormControl>
          <UiFormControl id="email" isRequired isInvalid={!!validation?.errors?.email}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Email address
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.email}
              onChange={({target}) => setInputs({email: target.value})}
              fontWeight="600"
            />
            {validation?.errors?.email && <UiFormErrorMessage>{validation?.errors?.email[0]}</UiFormErrorMessage>}
          </UiFormControl>
          <UiFormControl id="phoneNumber" isRequired isInvalid={!!validation?.errors?.phoneNumber}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Phone number
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.phoneNumber}
              onChange={({target}) => setInputs({phoneNumber: target.value})}
              fontWeight="600"
              type="number"
            />
            {validation?.errors?.phoneNumber && (
              <UiFormErrorMessage>{validation?.errors?.phoneNumber[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiCard>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire3Screen;
